import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Spin,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useHistory, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import callApi from '../../Api/config';
import { toastError, toastSuccess } from '../../service/Toast';
import { InvertFillButton } from '../../components/InvertFillButton';
import '../../components/style.scss';

const { Title, Text } = Typography;

type WithdrawRequest = {
  id: string;
  withdrawId: string;
  partnerId: string;
  partnerName: string;
  companyName: string;
  email: string;
  withdrawAmount: number;
  status: 'pending' | 'approved' | 'processed' | 'rejected';
  paymentMethod?: string;
  processingDate?: string;
  processedBy?: string;
  notes?: string;
  createdAt: string;
};

type PartnerInfo = {
  totalEarnings: number;
  totalWithdrawn: number;
  pendingAmount: number;
  remainingBalance: number;
  commissionType: string;
  commissionValue: number;
  memberSince: string;
  paymentDetails?: {
    zelle?: string;
    venmo?: string;
    upi?: string;
    bank?: {
      beneficiaryName?: string;
      bankName?: string;
      accountNumber?: string;
      ifscSwift?: string;
    };
  };
};

const statusColors: Record<string, string> = {
  pending: 'orange',
  approved: 'blue',
  processed: 'green',
  rejected: 'red',
};

const cardTitleStyle = { color: '#004195' };

type B2BWithdrawDetailProps = {
  requestId?: string;
  onClose?: () => void;
  onSaved?: () => void;
};

const B2BWithdrawDetail: React.FC<B2BWithdrawDetailProps> = ({ requestId, onClose, onSaved }) => {
  const routeParams = useParams<{ id?: string }>();
  const id = requestId ?? routeParams.id;
  const history = useHistory();
  const [form] = Form.useForm();

  const [request, setRequest] = useState<WithdrawRequest | null>(null);
  const [partner, setPartner] = useState<PartnerInfo | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<WithdrawRequest['status']>('pending');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const res = await callApi(`/affiliate/withdraw/${id}`, 'GET');
      if (res?.data?.isSuccess) {
        const payload = res.data?.data;
        setRequest(payload?.request ?? null);
        setPartner(payload?.partner ?? null);
        setSelectedStatus(payload?.request?.status ?? 'pending');
        form.setFieldsValue({
          status: payload?.request?.status,
          paymentMethod: payload?.request?.paymentMethod,
          processingDate: payload?.request?.processingDate
            ? dayjs(payload.request.processingDate)
            : null,
          processedBy: payload?.request?.processedBy,
          notes: payload?.request?.notes,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      const payload: any = {
        status: values.status,
        paymentMethod: values.paymentMethod,
        processedBy: values.processedBy,
        notes: values.notes,
      };
      if (values.processingDate) {
        payload.processingDate = values.processingDate.format('YYYY-MM-DD');
      }
      const res = await callApi(`/affiliate/withdraw/${id}`, 'PATCH', payload);
      if (res?.data?.isSuccess) {
        toastSuccess('Withdrawal request updated successfully');
        fetchDetail();
        onSaved?.();
      } else {
        toastError(res?.data?.message ?? 'Update failed');
      }
    } catch {
      toastError('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}
      >
        <Spin size="large" />
      </div>
    );
  }

  const handleBack = () => {
    if (onClose) {
      onClose();
    } else {
      history.goBack();
    }
  };

  if (!request) {
    return (
      <div style={{ padding: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack} style={{ marginBottom: 16 }}>
          Back
        </Button>
        <Title level={4}>Withdrawal request not found.</Title>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '16px',
        borderRadius: 8,
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Withdrawal Request Details
          </Title>
        </div>
      </div>

      <Row gutter={20}>
        {/* Left column â€” sections 1â€“3 */}
        <Col span={16}>
          {/* 1. Partner Information */}
          <Card
            title={<span style={cardTitleStyle}>Partner Information</span>}
            style={{ marginBottom: 16, borderRadius: 8 }}
            bordered
          >
            <Descriptions column={3} size="small">
              <Descriptions.Item label="Partner">
                <Text strong>{request.partnerName}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Company">
                <Text>{request.companyName}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                <Text>{request.email}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* 2. Withdrawal Information */}
          <Card
            title={<span style={cardTitleStyle}>Withdrawal Information</span>}
            style={{ marginBottom: 16, borderRadius: 8 }}
            bordered
          >
            <Descriptions column={3} size="small">
              <Descriptions.Item label="Withdraw ID">
                <Text code>{request.withdrawId}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Withdraw Date">
                {new Date(request.createdAt).toLocaleDateString()}
              </Descriptions.Item>
              <Descriptions.Item label="Withdraw Amount">
                <Text strong style={{ fontSize: 18, color: '#16a34a' }}>
                  ${Number(request.withdrawAmount).toLocaleString()}
                </Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* 3. Status Management */}
          <Card
            style={{ borderRadius: 8 }}
            title={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 16,
                  width: '100%',
                }}
              >
                <span style={cardTitleStyle}>Status Management</span>
                <div
                  style={{
                    padding: '4px 10px',
                    borderRadius: 4,
                    background:
                      selectedStatus === 'pending'
                        ? '#fef3c7'
                        : selectedStatus === 'processed'
                          ? '#d1fae5'
                          : selectedStatus === 'approved'
                            ? '#dbeafe'
                            : '#fee2e2',
                    color:
                      selectedStatus === 'pending'
                        ? '#d97706'
                        : selectedStatus === 'processed'
                          ? '#15803d'
                          : selectedStatus === 'approved'
                            ? '#1d4ed8'
                            : '#b91c1c',
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Current Status: {selectedStatus.toUpperCase()}
                </div>
              </div>
            }
            bordered
          >
            <Form form={form} layout="vertical" onFinish={handleSave}>
              <Form.Item name="status" rules={[{ required: true }]} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <span style={{ flex: '0 0 auto', fontWeight: 500 }}>Status:</span>
                  <Radio.Group
                    onChange={event => setSelectedStatus(event.target.value)}
                    options={[
                      { label: 'Pending', value: 'pending' },
                      { label: 'Approved', value: 'approved' },
                      { label: 'Processed', value: 'processed' },
                      { label: 'Rejected', value: 'rejected' },
                    ]}
                  />
                </div>
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item label="Processing Date" name="processingDate">
                    <DatePicker className="content-rounded-field" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Payment Method"
                    name="paymentMethod"
                    rules={[{ required: true, message: 'Please select payment method' }]}
                  >
                    <Select
                      className="content-rounded-field"
                      placeholder="Select payment method"
                      dropdownClassName="select-checkbox-dropdown"
                    >
                      <Select.Option value="Bank Transfer">Bank Transfer</Select.Option>
                      <Select.Option value="UPI">UPI</Select.Option>
                      <Select.Option value="Zelle">Zelle</Select.Option>
                      <Select.Option value="Venmo">Venmo</Select.Option>
                      <Select.Option value="PayPal">PayPal</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Withdraw By" name="processedBy">
                    <Input className="content-rounded-field" placeholder="Enter name" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Notes" name="notes">
                <Input.TextArea
                  className="content-rounded-field"
                  rows={3}
                  placeholder="Optional notes..."
                />
              </Form.Item>

              <Form.Item>
                <InvertFillButton
                  variant="blue"
                  size="md"
                  type="submit"
                  disabled={saving}
                  style={{ width: '100%' }}
                >
                  {saving ? 'Updating...' : 'Update Request'}
                </InvertFillButton>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Right column â€” sections 4â€“6 */}
        <Col span={8}>
          {/* 4. Earnings Overview */}
          <Card
            title={<span style={cardTitleStyle}>Earnings Overview</span>}
            style={{ marginBottom: 16, borderRadius: 8 }}
            bordered
          >
            {partner ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 12,
                }}
              >
                <div
                  style={{
                    background: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: 8,
                    padding: 12,
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    TOTAL EARNINGS
                  </Text>
                  <div
                    className="content-rounded-display"
                    style={{ fontSize: 22, fontWeight: 700, color: '#16a34a' }}
                  >
                    $
                    {Number(partner.totalEarnings).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                </div>
                <div
                  style={{
                    background: '#fff7ed',
                    border: '1px solid #fed7aa',
                    borderRadius: 8,
                    padding: 12,
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    TOTAL PAID OUT
                  </Text>
                  <div
                    className="content-rounded-display"
                    style={{ fontSize: 22, fontWeight: 700, color: '#ea580c' }}
                  >
                    $
                    {Number(partner.totalWithdrawn).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                </div>
                {Number(partner.pendingAmount) > 0 && (
                  <div
                    style={{
                      background: '#fefce8',
                      border: '1px solid #fde047',
                      borderRadius: 8,
                      padding: 12,
                    }}
                  >
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      PENDING / APPROVED
                    </Text>
                    <div
                      className="content-rounded-display"
                      style={{ fontSize: 22, fontWeight: 700, color: '#ca8a04' }}
                    >
                      $
                      {Number(partner.pendingAmount).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      Reserved for in-progress requests
                    </Text>
                  </div>
                )}
                <div
                  style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    padding: 12,
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    AVAILABLE BALANCE
                  </Text>
                  <div
                    className="content-rounded-display"
                    style={{ fontSize: 22, fontWeight: 700, color: '#1e293b' }}
                  >
                    $
                    {Number(partner.remainingBalance).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    After all pending deductions
                  </Text>
                </div>
              </div>
            ) : (
              <Text type="secondary">Earnings data unavailable.</Text>
            )}
          </Card>

          {/* 5. Commission Structure */}
          <Card
            title={<span style={cardTitleStyle}>Commission Structure</span>}
            style={{ marginBottom: 16, borderRadius: 8 }}
            bordered
          >
            {partner ? (
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="Commission Type">
                  <div className="content-rounded-display">
                    <Text style={{ textTransform: 'capitalize' }}>{partner.commissionType}</Text>
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Commission Rate">
                  <div className="content-rounded-display">
                    <Text strong style={{ color: '#2563eb' }}>
                      {partner.commissionType === 'percentage'
                        ? `${partner.commissionValue}%`
                        : `$${partner.commissionValue}`}
                    </Text>
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Member Since">
                  <div className="content-rounded-display">
                    {partner.memberSince
                      ? new Date(partner.memberSince).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : '—'}
                  </div>
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Text type="secondary">Commission data unavailable.</Text>
            )}
          </Card>

          {/* 6. Saved Payment Methods */}
          <Card
            title={<span style={cardTitleStyle}>Saved Payment Methods</span>}
            style={{ marginBottom: 16, borderRadius: 8 }}
            bordered
          >
            {partner?.paymentDetails ? (
              <Descriptions column={1} size="small">
                {partner.paymentDetails.zelle && (
                  <Descriptions.Item label="Zelle ID">
                    {partner.paymentDetails.zelle}
                  </Descriptions.Item>
                )}
                {partner.paymentDetails.venmo && (
                  <Descriptions.Item label="Venmo ID">
                    {partner.paymentDetails.venmo}
                  </Descriptions.Item>
                )}
                {partner.paymentDetails.upi && (
                  <Descriptions.Item label="UPI ID">{partner.paymentDetails.upi}</Descriptions.Item>
                )}
                {partner.paymentDetails.bank?.bankName && (
                  <>
                    <Descriptions.Item label="Bank Name">
                      {partner.paymentDetails.bank.bankName}
                    </Descriptions.Item>
                    <Descriptions.Item label="Beneficiary">
                      {partner.paymentDetails.bank.beneficiaryName}
                    </Descriptions.Item>
                    <Descriptions.Item label="Account No.">
                      {partner.paymentDetails.bank.accountNumber}
                    </Descriptions.Item>
                    <Descriptions.Item label="IFSC/SWIFT">
                      {partner.paymentDetails.bank.ifscSwift}
                    </Descriptions.Item>
                  </>
                )}
                {!partner.paymentDetails.zelle &&
                  !partner.paymentDetails.venmo &&
                  !partner.paymentDetails.upi &&
                  !partner.paymentDetails.bank?.bankName && (
                    <Descriptions.Item>
                      <Text type="secondary">No payment methods saved.</Text>
                    </Descriptions.Item>
                  )}
              </Descriptions>
            ) : (
              <Text type="secondary">No payment methods on record.</Text>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default B2BWithdrawDetail;
